// @flow
import React from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import styled from 'styled-components';
import { Link, Redirect } from 'react-router-dom';

import {
	getFinalsRounds,
} from '../../helpers/league';
import { type TRound } from '../../modules/types';

import * as selectors from '../../modules/selectors';
import * as actions from '../../modules/actions';
import colors from '../../assets/css/colors';

import {
	AdsContainer,
	Ad,
	TopBar,
	RoundSelector,
	OneColumnLayout,
	DraftLegend,
	ButtonSecondaryLink
} from '../../components';
import { isDraftLeagueByeRound } from '../../helpers/complex/leagues';

import JsonFetcher from '../../components/utils/JsonFetcher';
import Fixture from '../../components/Fixture';
import { getTeamProjectedScore, getTeamScoreFromLadder } from '../../helpers/team';
import { getLeagueRoundAliases } from '../../helpers/league';
import ArrowLeft from '../../components/Icons/ArrowLeft';
import ArrowRight from '../../components/Icons/ArrowRight';
import { isRoundActive } from '../../helpers/rounds';
import PlayersInfo from './matchup/playersInfo';
import TeamsComparisonTable from './matchup/teamsComparisonTable';

const Wrapper = styled.div`
	box-shadow: 0px 0px 6px 3px #e4e4e4;
	margin: 40px 0;
	background-color: white;
`;

const Head = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	& > :nth-child(2) {
		flex-grow: 1;
	}
`;

const NextPrevMatchUp = styled(Link)`
	width: 25px;
	height: 120px;
	border: none;
	background: transparent;
	display: flex;
	justify-content: center;
	align-items: center;
	svg {
		height: 12px;
		width: 12px;
		fill: ${colors.darkGrey};
	}
	&:hover {
		svg {
			fill: ${colors.grey};
		}
	}
	${({ disabled }) => disabled && `
		pointer: events;
		display: none;
		svg {
			fill: white;
		}
	`};
`;

const PlayersInfoWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	background: ${colors.lightGreen};
`;

const ByeRoundSection = styled.section`
	text-align: center;
	background-color: white;
	width: 100%;
	border-radius: 5px;
	padding: 30px;
	font-size: 15px;
	font-weight: 700;
	color: ${colors.darkGrey};
	p {
		margin-bottom: 20px;
	}

	a {
		max-width: 20em;
		position: relative;
		margin: 0 auto;
	}
`;

type Props = {
	selected_league_id: string,
	match: Object,
	league: Object,
	fetchLeague: typeof actions.leagueDraft.showDraftLeague,
	fetchLadder: typeof actions.leagueDraft.ladderDraftLeague,
	fetchLeagueRosters: typeof actions.leagueDraft.fetchLeagueRosters,
	fetchPlayers: typeof actions.players.fetchPlayers,
	rounds: Array<TRound>,
	round: TRound,
	history: Object,
	coach_players_by_id: Object,
	has_assistant_coach: boolean,
	rounds_by_id: Object,
	ladder_teams_by_id: Object,
	round_id?: number,
	home_team_id?: number,
	home_team: Object,
	away_team_id?: number,
	away_team: Object,
	rosters: Object,
	user: Object,
	players: Object,
	league_rounds: Array<TRound>,
	ladder_by_id: Object,
	actual_round: TRound,
	positions: Array<Object>,
	data_has_loaded: boolean
}

type State = {
	interval?: IntervalID | null
}

const buildFantasyMatchupsUrlPathname = (league_id, round_id, home_team_id, away_team_id) => {
	const path_sections = [
		'/league/',
		league_id,
		'/matchup',
	];

	if (home_team_id && away_team_id) {
		path_sections.push(`/${home_team_id}`);
		path_sections.push(`/${away_team_id}`);
	}
	if(round_id) {
		path_sections.push(`/${round_id}`);
	}

	return path_sections.join('');
};

class MatchUp extends React.Component<Props, State> {

	state = {
		interval: null
	}

	constructor(props) {
		super(props);

		_.bindAll(this,
			'renderTeamsFixture',
			'getPrevFixture',
			'getNextFixture',
			'getUsersFixtureForRound',
			'fetchData'
		);
	}

	componentDidMount() {
		this.fetchData();

		const interval = setInterval(this.fetchData, 60000);
		this.setState({ interval });
	}

	componentDidUpdate(prev_props) {
		const {
			fetchLeagueRosters,
			selected_league_id,
			match: { params: { round_id } }
		} = this.props;
		if(!_.isEqual(prev_props.match.params, this.props.match.params)) {
			fetchLeagueRosters({ league_id: selected_league_id, round: round_id });
		}
	}

	componentWillUnmount() {
		// $FlowFixMe
		clearInterval(this.state.interval);
	}

	fetchData() {
		const {
			fetchLadder,
			fetchLeague,
			fetchLeagueRosters,
			fetchPlayers,
			selected_league_id,
			match
		} = this.props;

		fetchPlayers();
		fetchLeague({ id: selected_league_id });
		fetchLadder({ league_id: selected_league_id });
		fetchLeagueRosters({ league_id: selected_league_id, round: match.params.round_id });
	}

	getRoundFixtures(round_id) {
		const { league } = this.props;
		return _.get(league, ['fixture', round_id], []);
	}

	get finals_rounds() {
		const { league, rounds } = this.props;
		return getFinalsRounds(league, rounds);
	}

	get is_finals_round() {
		const { round_id } = this.props;
		return this.finals_rounds.includes(round_id);
	}

	get has_fixtures() {
		return Boolean(parseInt(this.props.match.params.home_team_id,10))
			&& Boolean(parseInt(this.props.match.params.away_team_id, 10));
	}

	get show_finals_not_determined_message() {
		return this.is_finals_round && !this.has_fixtures;
	}

	get round() {
		const {
			league_rounds,
			round_id
		} = this.props;

		return _.find(league_rounds, round => round.id === round_id);
	}


	get next_fixture() {
		const { round_id, home_team_id, away_team_id  } = this.props;
		const round_fixtures = this.getRoundFixtures(round_id);

		const current_fixture_index = round_fixtures.findIndex(fixture => {
			return fixture[0] === home_team_id &&
				fixture[1] === away_team_id;
		});
		const next_fixture = round_fixtures[current_fixture_index + 1];

		if (next_fixture) {
			return [
				next_fixture[0],
				next_fixture[1]
			];
		}

		return null;
	}

	get prev_fixture() {
		const { round_id, home_team_id, away_team_id  } = this.props;
		const round_fixtures = this.getRoundFixtures(round_id);

		const current_fixture_index = round_fixtures.findIndex(fixture => {
			return fixture[0] === home_team_id &&
				fixture[1] === away_team_id;
		});
		const prev_fixture = round_fixtures[current_fixture_index - 1];

		if (prev_fixture) {
			return [
				prev_fixture[0],
				prev_fixture[1]
			];
		}

		return null;
	}

	getPrevFixture() {
		const { league, round_id } = this.props;

		const prev_fixture = this.prev_fixture;

		if (prev_fixture) {
			return buildFantasyMatchupsUrlPathname(
				league.id,
				round_id,
				prev_fixture[0],
				prev_fixture[1]
			);
		}
	}

	getNextFixture() {
		const { league, round_id } = this.props;

		const next_fixture = this.next_fixture;

		if (next_fixture) {
			return buildFantasyMatchupsUrlPathname(
				league.id,
				round_id,
				next_fixture[0],
				next_fixture[1]
			);
		}
	}

	get default_matchup_url() {
		const {
			league,
			rosters,
			user
		} = this.props;

		const round_id = this.props.round_id?this.props.round_id:this.props.actual_round.id;

		if (_.isEmpty(rosters) === false) {
			const round_fixtures = this.getRoundFixtures(round_id);
			const user_fixture = round_fixtures.find(fixture => {
				const home_team = _.get(rosters, fixture[0]);
				const away_team = _.get(rosters, fixture[1]);

				return _.get(home_team, 'user_id') === user.id ||
					_.get(away_team, 'user_id') === user.id;
			});

			if (Array.isArray(user_fixture)) {
				const home_team_id = user_fixture[0];
				const away_team_id = user_fixture[1];

				return buildFantasyMatchupsUrlPathname(
					league.id,
					round_id,
					home_team_id,
					away_team_id
				);
			}
		}

		return null;
	}

	get is_matchup_round_id_set() {
		const {
			round_id,
		} = this.props;

		return Boolean(round_id);
	}

	get is_round_id_set() {
		const {
			round_id,
		} = this.props;

		return Boolean(round_id);
	}

	get is_team_ids_set() {
		const {
			home_team_id,
			away_team_id,
		} = this.props;

		return Boolean(home_team_id) && Boolean(away_team_id);
	}

	get redirect_to_default_matchup_url() {
		return !this.is_team_ids_set && !this.is_round_id_set && this.default_matchup_url;
	}

	get redirect_to_default_round_matchup_url() {
		return !this.is_team_ids_set && this.is_round_id_set && this.default_round_matchup_url;
	}

	get default_round_matchup_url() {
		const {
			league,
			round_id,
			rosters,
			user
		} = this.props;

		if (_.isEmpty(rosters) === false) {
			const round_fixtures = this.getRoundFixtures(round_id);
			const user_fixture = round_fixtures.find(fixture => {
				const home_team = _.get(rosters, fixture[0]);
				const away_team = _.get(rosters, fixture[1]);

				return _.get(home_team, 'user_id') === user.id ||
					_.get(away_team, 'user_id') === user.id;
			});

			if (Array.isArray(user_fixture)) {
				const home_team_id = user_fixture[0];
				const away_team_id = user_fixture[1];

				return buildFantasyMatchupsUrlPathname(
					league.id,
					round_id,
					home_team_id,
					away_team_id
				);
			}
		}

		return null;
	}

	get is_bye() {
		const {
			league,
		} = this.props;
		if(this.round){
			return isDraftLeagueByeRound(league, this.round);
		}
		return false;
		
	}

	mergeTeamData(team) {
		const {
			ladder_by_id,
		} = this.props;

		if(team) {
			return {
				...team,
				...ladder_by_id[team.id],
			};
		}
		else {
			return {};
		}
	}

	getUsersFixtureForRound(round_id): any {
		const { league: { team_id, fixture } } = this.props;
		const round_id_string = round_id.toString();

		if(Object.keys(fixture).includes(round_id_string)) {
			const round_fixture = _.get(fixture, round_id.toString());
			const f = round_fixture.find(f => f.includes(parseInt(team_id, 10)));
			return f;
		}
		return [0, 0];
	}

	// eslint-disable-next-line complexity
	renderTeamsFixture() {
		if(!this.props.match.params.home_team_id || !this.props.match.params.away_team_id) {
			return null;
		}

		const team_ids =
			[this.props.match.params.home_team_id, this.props.match.params.away_team_id];

		const {
			ladder_teams_by_id,
			league,
			has_assistant_coach,
			coach_players_by_id,
			players,
			round_id,
			rosters,
			rounds
		} = this.props;

		const round = _.find(rounds, { id: round_id });
		if (round === undefined) {
			return null;
		}

		// $FlowFixMe
		if(_.some([
			ladder_teams_by_id,
			rounds,
			rosters[team_ids[0]]
		], _.isEmpty)) {
			return null;
		}

		const home_team = rosters[team_ids[0]];
		const away_team = rosters[team_ids[1]];
		const is_round_active = isRoundActive(round);

		// $FlowFixMe
		const home_points = getTeamScoreFromLadder({
			team: home_team,
			is_active_match: is_round_active,
			round,
			players
		});

		// $FlowFixMe
		const away_points = getTeamScoreFromLadder({
			team: away_team,
			is_active_match: is_round_active,
			round,
			players
		});

		if(
			_.isEmpty(home_team)||
			_.isEmpty(home_team.lineup) ||
			_.isEmpty(away_team)||
			_.isEmpty(away_team.lineup)
		) {
			return null;
		}


		const home_lineup_project_score =
			getTeamProjectedScore(home_team.lineup, players, coach_players_by_id, league, round);
		const away_lineup_project_score =
			getTeamProjectedScore(away_team.lineup, players, coach_players_by_id, league, round);

		const fixture = {
			home: {
				id: home_team.id,
				name: home_team.name,
				firstname: home_team.firstname,
				lastname: home_team.lastname,
				points: Number(home_points),
				projected_points: home_lineup_project_score,
				lineup: home_team.lineup,
				rank: home_team.rank,
				user_id: home_team.user_id,
				avatar_version: home_team.avatar_version,
				scoreflow: home_team.scoreflow,
			},
			away: {
				id: away_team.id,
				name: away_team.name,
				firstname: away_team.firstname,
				lastname: away_team.lastname,
				points: Number(away_points),
				projected_points: away_lineup_project_score,
				lineup: away_team.lineup,
				rank: away_team.rank,
				user_id: away_team.user_id,
				avatar_version: away_team.avatar_version,
				scoreflow: away_team.scoreflow
			}
		};

		return (
			<Fixture
				key={team_ids.toString()}
				fixture={fixture}
				league={league}
				round={round}
				has_assistant_coach={has_assistant_coach}
				players={players}
				versus
			/>
		);
	}

	// eslint-disable-next-line complexity
	render() {
		const {
			match,
			league,
			home_team,
			away_team,
			players,
			actual_round,
			has_assistant_coach,
			positions,
			round_id,
			rounds,
			league_rounds,
		} = this.props;

		// if(!data_has_loaded) {
		// 	return <Preloader />;
		// }

		if (this.redirect_to_default_matchup_url) {
			// $FlowFixMe
			return <Redirect to={this.default_matchup_url} />;
		}
		else if (this.redirect_to_default_round_matchup_url) {
			// $FlowFixMe
			return <Redirect to={this.default_round_matchup_url} />;
		}

		const round = league_rounds.find(round => {
			return round_id === round.id;
		});

		const json_files_to_fetch = ['rounds', 'players', 'squads'];
		if(has_assistant_coach) {
			json_files_to_fetch.push('coach_players');
		}

		return (
			<div>
				<JsonFetcher
					fetch={json_files_to_fetch}
					interval={60000}
				/>
				<TopBar league={league} />
				<RoundSelector
					selected={parseInt(match.params.round_id, 10)}
					rounds={league_rounds}
					handleClick={round_id => {
						const fixture = this.getUsersFixtureForRound(round_id);
						if(fixture) {
							this.props.history.push(`/league/${match.params.league_id}/matchup/${fixture[0]}/${fixture[1]}/${round_id}`); // eslint-disable-line max-len
						}
					}}
					round_aliases={getLeagueRoundAliases(league, rounds)}
				/>
				<AdsContainer>
					<Ad
						adUnit='matchup'
						size='top'
					/>
				</AdsContainer>
				<OneColumnLayout>
					{this.show_finals_not_determined_message
						? (
							<Wrapper>
								<ByeRoundSection>
									<p>
										Finals Matchups for your league are yet to be determined
									</p>
									<ButtonSecondaryLink to={`/league/${match.params.league_id}`}>
										Go to your league overview
									</ButtonSecondaryLink>
								</ByeRoundSection>
							</Wrapper>
						)
						: null
					}
					{this.is_bye && !this.show_finals_not_determined_message ? (
						<Wrapper>
							<ByeRoundSection>
								<p>
									No matchups in Round {match.params.round_id}{" "}
									due to your leagues bye settings.
								</p>
								<ButtonSecondaryLink to={`/league/${match.params.league_id}`}>
									Go to your league overview
								</ButtonSecondaryLink>
							</ByeRoundSection>
						</Wrapper>
					) : null}

					{!this.is_bye && !this.show_finals_not_determined_message ? (
						<Wrapper>
							<Head>
								<NextPrevMatchUp
									to={this.getPrevFixture() || './'}
									disabled={!this.prev_fixture}
								>
									<ArrowLeft />
								</NextPrevMatchUp>
								<div>
									{this.renderTeamsFixture()}
								</div>
								<NextPrevMatchUp
									to={this.getNextFixture() || './'}
									disabled={!this.next_fixture}
								>
									<ArrowRight />
								</NextPrevMatchUp>
							</Head>
							<PlayersInfoWrapper>
								<PlayersInfo
									opponent={this.mergeTeamData(home_team)}
									players_by_id={players}
									// $FlowFixMe
									round={this.round}
								/>
								<PlayersInfo
									opponent={this.mergeTeamData(away_team)}
									players_by_id={players}
									// $FlowFixMe
									round={this.round}
									right_align
								/>
							</PlayersInfoWrapper>
							<TeamsComparisonTable
								away_team={away_team}
								home_team={home_team}
								players={players}
								openPlayerPopup={() => console.log('popup')}
								actual_round={actual_round}
								has_assistant_coach={has_assistant_coach}
								league={league}
								positions={positions}
								// $FlowFixMe
								round={round}
								rounds={rounds}
							/>
						</Wrapper>
					) : null}
					<DraftLegend />
				</OneColumnLayout>
			</div>
		);
	};
}

const mapStateToProps = (state, props) => {
	const {
		match: { params: { league_id, round_id, home_team_id, away_team_id } }
	} = props;



	const loading_data = [
		!state.leagues.ladder.is_pending,
		!state.leagues.show.is_pending,
		!state.leagues.rosters.is_pending,
		!state.players.is_pending,
		!state.rounds.is_pending,
		!state.squads.is_pending,
		!_.isEmpty(state.leagues.teams_by_id),
		!_.isEmpty(selectors.leagueDraft.getLeague(state, props))
	];


	return {
		selected_league_id: league_id && parseInt(league_id, 10),
		round_id: round_id && parseInt(round_id, 10),
		home_team_id: home_team_id && parseInt(home_team_id, 10),
		away_team_id: away_team_id && parseInt(away_team_id, 10),
		home_team: state.leagues.teams_by_id[home_team_id],
		away_team: state.leagues.teams_by_id[away_team_id],
		teams_by_id: state.leagues.teams_by_id,
		ladder_by_id: selectors.leagueDraft.ladder.getById(state),
		players: selectors.players.getExtendedPlayersById(state, props),
		coach_players_by_id: selectors.players.getCoachPlayersById(state),
		positions: selectors.positions.getPositionsArray(state),
		playerStatuses: state.playerStatuses,
		league: selectors.leagueDraft.getLeague(state, props),
		user: selectors.getUser(state),
		has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
		league_rounds: selectors.getLeagueRoundsWithMatches(state, props),
		rosters: selectors.leagueDraft.rosters.getById(state),
		actual_round: selectors.rounds.getActualRound(state),
		data_has_loaded: _.every(loading_data),
		ladder_teams_by_id: selectors.leagueDraft.ladder.getById(state),
		rounds_by_id: state.rounds.by_id,
		rounds: selectors.rounds.getRoundsWithMatches(state)
	};
};

const mapDispatchToProps = {
	fetchLeague: actions.leagueDraft.showDraftLeague,
	fetchLeagueRosters: actions.leagueDraft.fetchLeagueRosters,
	fetchLadder: actions.leagueDraft.ladderDraftLeague,
	fetchTeam: actions.teamsDraft.fetchTeam,
	fetchFavourites: actions.players.fetchFavourites,
	fetchPlayers: actions.players.fetchPlayers
};

export default connect(mapStateToProps, mapDispatchToProps)(MatchUp);


// WEBPACK FOOTER //
// ./src/pages/league/MatchUp.js